import { appConfigStore } from 'Models/AppConfigStore';
import { SeatingTabData, seatingIsEmpty, selectableSeatsForClass } from 'Services/Api/_HumanWritten/SeatingService';
import { useSeatingStore } from 'Stores/SeatingStore';
import { VisibilityStatus } from '../../BookingWizard';
import { BookingWizardData, getPassengerTickets } from '../../BookingWizardData/BaseBookingWizardData';
import { PassengerDetail } from '../../BookingWizardData/PassengerDetail';
import passengerTypeStore from 'Models/PassengerTypeStore';
import { PassengerField } from 'Models/Enums';

/**
 * The seating data already fetched for the wizard's selected trip, if any.
 *
 * <p>Read from the store rather than fetched, because tab visibility and validation are computed
 * synchronously while the wizard builds its step list. The fetch is `useSeatingTabLegs`, called from the
 * wizard shell — it cannot live in the step alone, because the step is only rendered once this data
 * says it should be, so the step would be excluded forever waiting on a fetch it never makes.</p>
 *
 * <p>Until the fetch lands the step stays excluded, which is the safe default — a step that appears
 * a moment late is better than one that blocks the flow on a ferry with no classes at all.</p>
 */
export function getCachedSeatingData(wizardData: BookingWizardData): SeatingTabData | undefined {
	return getCachedSeatingDataForTrip(wizardData?.departingJourney?.tripId);
}

/** The same, for either leg's own sailing. Both legs are mirrored into the store by the fetch. */
export function getCachedSeatingDataForTrip(tripId: string | undefined): SeatingTabData | undefined {
	if (!tripId) {
		return undefined;
	}
	return useSeatingStore.getState().byTripId[tripId];
}

/**
 * The step is shown only when the selected trip's ferry actually offers something to choose — at
 * least one class or one seat. Every client that has not configured either keeps today's wizard.
 */
export function getSeatingTabVisibility(wizardData: BookingWizardData): VisibilityStatus {
	if (!appConfigStore.passengerClassesEnabled) {
		return VisibilityStatus.EXCLUDED;
	}
	/*
		A booking that already holds a fare type or a seat needs this step whatever the fetch has
		returned so far — it is proof the vessel sells them, which is the only thing the store is
		being asked.

		Without this, an edit sent straight to the seating step was bounced: the wizard skips an
		excluded step and walks forward to the next one, the step is excluded until its fetch lands,
		and the URL it was bounced to is where the booker stayed.
	*/
	if (bookingHoldsSeating(wizardData)) {
		return VisibilityStatus.VISIBLE;
	}

	// Either leg. Asking only the departing one meant a return booking whose homeward vessel is the
	// one selling fare types never saw the step at all.
	const legs = [getCachedSeatingData(wizardData), getCachedSeatingDataForTrip(wizardData?.returningJourney?.tripId)];
	return legs.some(data => !seatingIsEmpty(data)) ? VisibilityStatus.VISIBLE : VisibilityStatus.EXCLUDED;
}

/** Whether any passenger on the booking already holds a fare type or a seat, on either leg. */
export function bookingHoldsSeating(wizardData: BookingWizardData): boolean {
	return collectPassengers(wizardData).some(
		passenger =>
			!!passenger.passengerClassId ||
			!!passenger.returningPassengerClassId ||
			!!passenger.seatId ||
			!!passenger.returningSeatId,
	);
}

/** Every passenger on the booking, across all eight type slots. */
export function collectPassengers(wizardData: BookingWizardData): PassengerDetail[] {
	const out: PassengerDetail[] = [];
	passengerTypeStore.passengerTypes.forEach(passengerType => {
		const field = passengerTypeStore.getWizardDataProperty(passengerType.passengerTypeKey) as PassengerField;
		const tickets = getPassengerTickets(wizardData.innerWizardData, field);
		if (tickets) {
			out.push(...tickets);
		}
	});
	return out;
}

/**
 * What still has to be chosen before the booker can continue, or null when nothing does.
 *
 * <p><b>A fare type is required. A seat is not.</b> The fare type is what the passenger is being
 * charged for and the server prices the ticket from it, so a booking cannot leave this step without
 * one. A seat is a preference: the step says so in as many words at the foot of the list — seats are
 * assigned automatically at check-in at no charge — and check-in does exactly that, seating the
 * party together on arrival. Demanding one here made the wizard contradict its own notice and
 * refuse a booking the system is built to take.</p>
 */
export function validateSeating(wizardData: BookingWizardData): string | null {
	// Nothing to validate when the step is not shown.
	if (!appConfigStore.passengerClassesEnabled) {
		return null;
	}
	const data = getCachedSeatingData(wizardData);
	if (seatingIsEmpty(data) || !data) {
		return null;
	}

	const passengers = collectPassengers(wizardData);
	if (passengers.length === 0) {
		return null;
	}

	/*
		Asked of each leg the booking is on, not of the departing one twice.

		A passenger holds a fare type and a seat per sailing, in their own pair of fields. Reading
		only `passengerClassId` let a return booking leave the step with nobody assigned a fare type
		on the homeward leg — the step showed `Returning · 1 to do` and `Continue` went through
		anyway, because the validator never looked at that leg. The duplicate-seat check had the same
		hole: two passengers could be sent to one returning seat.
	*/
	const legs: {
		data: SeatingTabData | undefined;
		classIdOf: (p: PassengerDetail) => string | undefined;
		seatIdOf: (p: PassengerDetail) => string | undefined;
	}[] = [
		{
			data,
			classIdOf: p => p.passengerClassId,
			seatIdOf: p => p.seatId,
		},
		{
			data: getCachedSeatingDataForTrip(wizardData?.returningJourney?.tripId),
			classIdOf: p => p.returningPassengerClassId,
			seatIdOf: p => p.returningSeatId,
		},
	];

	for (const leg of legs) {
		const error = legError(leg, passengers);
		if (error) {
			return error;
		}
	}

	return null;
}

interface SeatingLeg {
	data: SeatingTabData | undefined;
	classIdOf: (p: PassengerDetail) => string | undefined;
	seatIdOf: (p: PassengerDetail) => string | undefined;
}

/** What one leg is still missing, or null where it is settled. */
function legError(leg: SeatingLeg, passengers: PassengerDetail[]): string | null {
	// A leg with nothing to choose — no return booking, or a vessel with neither classes nor
	// seats — has nothing to be missing.
	if (!leg.data || seatingIsEmpty(leg.data)) {
		return null;
	}

	if (leg.data.passengerClasses.length > 0 && passengers.some(p => !leg.classIdOf(p))) {
		return 'Choose a fare type for every passenger';
	}

	// Nothing here asks whether a passenger has a seat. Leaving the step unseated is a supported
	// outcome — see the note above — and it is the same outcome on a vessel that sells seats
	// without fare types, where the step's own notice says the same thing.

	// Two passengers on one seat is the one thing the picker cannot prevent on its own, because
	// each dropdown only knows its own passenger.
	const chosen = passengers.map(leg.seatIdOf).filter(Boolean) as string[];
	if (new Set(chosen).size !== chosen.length) {
		return 'Each passenger needs their own seat';
	}

	return null;
}

/**
 * Whether the homeward leg still has something for the booker to choose.
 *
 * <p>Asked by the wizard so that `Continue` on the outbound leg can move to the return one rather
 * than refuse with a message about a sailing that is not on screen. A booking with no return leg,
 * or one whose homeward vessel sells nothing to choose, answers false and the wizard goes on.</p>
 */
export function returningSeatingOutstanding(wizardData: BookingWizardData): boolean {
	const passengers = collectPassengers(wizardData);
	if (passengers.length === 0) {
		return false;
	}

	return (
		legError(
			{
				data: getCachedSeatingDataForTrip(wizardData?.returningJourney?.tripId),
				classIdOf: p => p.returningPassengerClassId,
				seatIdOf: p => p.returningSeatId,
			},
			passengers,
		) !== null
	);
}

/** A passenger who could have chosen a seat for nothing and has not, on one leg. */
export interface UnseatedFreeSeat {
	leg: 'departing' | 'returning';
	passenger: PassengerDetail;
}

/**
 * Passengers in a fare type where choosing a seat is free, who have not chosen one.
 *
 * <p>Not a validation error — a seat stays optional, and a passenger who chooses none is seated at
 * check-in. It is what the wizard warns about once on `Continue`, because leaving a free choice
 * untaken is usually an oversight rather than a decision, whereas passing over a paid seat is the
 * booker declining to spend money.</p>
 *
 * <p>A vessel with seats and no fare types counts too: its seats are free to choose.</p>
 */
export function unseatedFreeSeatPassengers(wizardData: BookingWizardData): UnseatedFreeSeat[] {
	if (!appConfigStore.passengerClassesEnabled) {
		return [];
	}
	const passengers = collectPassengers(wizardData);
	const legs: {
		leg: UnseatedFreeSeat['leg'];
		data: SeatingTabData | undefined;
		classIdOf: (p: PassengerDetail) => string | undefined;
		seatIdOf: (p: PassengerDetail) => string | undefined;
	}[] = [
		{
			leg: 'departing',
			data: getCachedSeatingData(wizardData),
			classIdOf: p => p.passengerClassId,
			seatIdOf: p => p.seatId,
		},
		{
			leg: 'returning',
			data: getCachedSeatingDataForTrip(wizardData?.returningJourney?.tripId),
			classIdOf: p => p.returningPassengerClassId,
			seatIdOf: p => p.returningSeatId,
		},
	];

	const out: UnseatedFreeSeat[] = [];
	legs.forEach(({ leg, data, classIdOf, seatIdOf }) => {
		if (!data || data.seats.length === 0) {
			return;
		}
		passengers.forEach(passenger => {
			if (seatIdOf(passenger)) {
				return;
			}
			const classId = classIdOf(passenger);
			if (data.passengerClasses.length > 0) {
				const passengerClass = data.passengerClasses.find(x => x.id === classId);
				if (!passengerClass?.allowsSeatSelection || (passengerClass.seatSelectionFee ?? 0) > 0) {
					return;
				}
			}
			// Only where there is a seat left to choose. A fare type sold out of seats, or one whose
			// seats are all on another booking, leaves nothing to warn about.
			if (selectableSeatsForClass(data.seats, classId).length === 0) {
				return;
			}
			out.push({ leg, passenger });
		});
	});
	return out;
}

/** The warning's identity: who it was about, one `leg:passengerId` entry each. */
export function unseatedFreeSeatSignature(unseated: UnseatedFreeSeat[]): string {
	return unseated.map(x => `${x.leg}:${x.passenger.id}`).join(',');
}

/**
 * Whether the booker has already been warned about everybody still unseated.
 *
 * <p>A subset rather than an exact match: seating two of the three passengers named must not bring
 * the same warning back for the third. Somebody new — a passenger moved into a free-seat fare type
 * since — is not covered and is warned about.</p>
 */
export function unseatedFreeSeatWarned(unseated: UnseatedFreeSeat[], warnedFor: string | null): boolean {
	if (unseated.length === 0) {
		return true;
	}
	if (!warnedFor) {
		return false;
	}
	const warned = new Set(warnedFor.split(','));
	return unseatedFreeSeatSignature(unseated)
		.split(',')
		.every(entry => warned.has(entry));
}
